import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  ChefHat, Lock, Mail, Loader2, ShieldCheck,
  KeyRound, RefreshCw, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

// ─── 6-digit OTP input (reutiliza el patrón de InvitationPage) ────────
function OtpInput({ value, onChange, disabled }) {
  const DIGITS = 6;
  const refs = Array.from({ length: DIGITS }, () => useRef(null)); // eslint-disable-line

  const digits = value.split("").concat(Array(DIGITS).fill("")).slice(0, DIGITS);

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      const next = value.slice(0, i) + value.slice(i + 1);
      onChange(next);
      if (i > 0) refs[i - 1].current?.focus();
      return;
    }
    if (!/^\d$/.test(e.key)) return;
    const next = (value.slice(0, i) + e.key + value.slice(i + 1)).slice(0, DIGITS);
    onChange(next);
    if (i < DIGITS - 1) refs[i + 1].current?.focus();
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, DIGITS);
    if (paste) {
      onChange(paste);
      refs[Math.min(paste.length, DIGITS - 1)].current?.focus();
    }
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="admin-otp-input-group">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={refs[i]}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={() => {}}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          data-testid={`admin-otp-digit-${i}`}
          className={`w-11 h-12 text-center text-xl font-bold rounded-lg border-2 bg-slate-800
            focus:outline-none transition-colors
            ${d ? "border-orange-500 text-white" : "border-slate-600 text-muted-foreground"}
            ${disabled ? "opacity-50 cursor-not-allowed" : "focus:border-orange-400"}`}
        />
      ))}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────
/** Wrapper seguro sobre fetch con credentials */
async function safeFetch(url, options = {}) {
  try {
    const resp = await fetch(url, {
      ...options,
      credentials: "include",
    });
    let body = {};
    try { body = await resp.json(); } catch { /* empty */ }
    return { ok: resp.ok, status: resp.status, body };
  } catch {
    return { ok: false, status: 0, body: {} };
  }
}

export default function AdminLoginPage() {
  const navigate = useNavigate();

  // Step 1: credentials
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loadingStep1, setLoadingStep1] = useState(false);

  // Step 2: OTP
  const [step, setStep] = useState(1); // 1 | 2
  const [sessionId, setSessionId] = useState("");
  const [otp, setOtp] = useState("");
  const [loadingStep2, setLoadingStep2] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(5);
  const [blocked, setBlocked] = useState(false);

  // Resend cooldown
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const cooldownRef = useRef(null);

  const startCooldown = useCallback((secs) => {
    setResendCooldown(secs);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(cooldownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  }, []);

  // ── Paso 1: validar credenciales ─────────────────────────────────────
  const handleCredentials = async (e) => {
    e.preventDefault();
    setLoadingStep1(true);
    try {
      const { ok, status, body } = await safeFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        }
      );
      if (!ok) {
        if (status === 0 || status >= 500) throw new Error("El servidor no responde. Intentalo de nuevo en unos segundos.");
        throw new Error(body.detail || "Credenciales invalidas");
      }

      setSessionId(body.session_id);
      setStep(2);
      startCooldown(30);
      toast.success(body.message || "Código enviado a tu email");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingStep1(false);
    }
  };

  // ── Paso 2: verificar OTP ─────────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (blocked) return;
    if (otp.length < 6) { toast.error("Introduce los 6 dígitos del código"); return; }

    setLoadingStep2(true);
    try {
      const { ok, status, body } = await safeFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/verify-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId, otp }),
        }
      );
      if (!ok) {
        if (status === 0 || status >= 500) { setOtp(""); throw new Error("El servidor no responde. Intentalo de nuevo."); }
        const match = body.detail?.match(/(\d+) intento/);
        if (match) setAttemptsLeft(parseInt(match[1], 10));
        if (status === 429) { setBlocked(true); setAttemptsLeft(0); }
        setOtp("");
        throw new Error(body.detail || "Codigo incorrecto");
      }

      toast.success("Bienvenido al panel de administración");
      navigate("/admin");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingStep2(false);
    }
  };

  // ── Reenviar OTP ──────────────────────────────────────────────────────
  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    try {
      const { ok, status, body } = await safeFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        }
      );
      if (!ok) {
        if (status === 0 || status >= 500) throw new Error("El servidor no responde. Intentalo de nuevo.");
        throw new Error(body.detail || "Error al reenviar");
      }

      setSessionId(body.session_id);
      setOtp("");
      setBlocked(false);
      setAttemptsLeft(5);
      startCooldown(60);
      toast.success("Nuevo código enviado");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResending(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      {/* Background grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:48px_48px]" />

      <div className="relative w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-3">
            <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30">
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">MenuGenius</span>
          </div>
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <ShieldCheck className="w-4 h-4 text-orange-400" />
            <p className="text-slate-400 text-sm">Panel de Administración</p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-5">
          {[1, 2].map((s) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
                ${step >= s ? "bg-orange-500 text-white" : "bg-slate-800 text-muted-foreground"}`}>
                {s}
              </div>
              <div className={`h-0.5 flex-1 rounded ${s === 1 ? (step > 1 ? "bg-orange-500" : "bg-slate-800") : "hidden"}`} />
            </div>
          ))}
          <div className="flex-1" />
        </div>

        {/* Card */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl">

          {step === 1 ? (
            /* ── Paso 1: credenciales ── */
            <>
              <h1 className="text-white text-xl font-semibold mb-1">Iniciar sesión</h1>
              <p className="text-muted-foreground text-sm mb-6">Acceso restringido al staff de MenuGenius</p>

              <form onSubmit={handleCredentials} className="space-y-4" data-testid="admin-credentials-form">
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-wide mb-1.5 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@menugenius.online"
                      required
                      data-testid="admin-email-input"
                      className="pl-9 bg-slate-800 border-slate-700 text-white placeholder:text-slate-600
                                 focus:border-orange-500 focus:ring-orange-500/20"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-wide mb-1.5 block">Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      data-testid="admin-password-input"
                      className="pl-9 bg-slate-800 border-slate-700 text-white placeholder:text-slate-600
                                 focus:border-orange-500 focus:ring-orange-500/20"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loadingStep1}
                  data-testid="admin-login-btn"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold
                             shadow-lg shadow-orange-500/20 transition-all mt-2"
                >
                  {loadingStep1 ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verificando...</>
                  ) : (
                    <>Continuar</>
                  )}
                </Button>
              </form>
            </>
          ) : (
            /* ── Paso 2: OTP ── */
            <>
              <div className="flex items-center gap-2 mb-1">
                <button
                  onClick={() => { setStep(1); setOtp(""); setBlocked(false); setAttemptsLeft(5); }}
                  data-testid="admin-otp-back-btn"
                  className="text-muted-foreground hover:text-slate-300 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <h1 className="text-white text-xl font-semibold">Verificación OTP</h1>
              </div>
              <p className="text-muted-foreground text-sm mb-6">
                Introduce el código de 6 dígitos enviado a <strong className="text-slate-300">{email}</strong>
              </p>

              {blocked && (
                <div className="flex items-start gap-3 p-3 rounded-xl bg-red-950/30 border border-red-900
                               mb-5" data-testid="admin-otp-blocked">
                  <ShieldCheck className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-400 text-sm font-semibold">Acceso bloqueado</p>
                    <p className="text-red-500/80 text-xs mt-0.5">
                      Demasiados intentos. Solicita un nuevo código.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleVerifyOtp} className="space-y-5" data-testid="admin-otp-form">
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-slate-400 text-xs uppercase tracking-wide">
                      Código de un solo uso
                    </Label>
                    {attemptsLeft < 5 && !blocked && (
                      <span className={`text-xs font-medium ${attemptsLeft <= 2 ? "text-red-400" : "text-amber-400"}`}
                            data-testid="admin-attempts-left">
                        {attemptsLeft} intento{attemptsLeft !== 1 ? "s" : ""} restante{attemptsLeft !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <OtpInput value={otp} onChange={setOtp} disabled={loadingStep2 || blocked} />
                  <p className="text-xs text-center text-muted-foreground">
                    Revisa tu bandeja de entrada (y la carpeta de spam)
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loadingStep2 || blocked || otp.length < 6}
                  data-testid="admin-otp-verify-btn"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold
                             shadow-lg shadow-orange-500/20 transition-all disabled:opacity-50"
                >
                  {loadingStep2 ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verificando...</>
                  ) : (
                    <><KeyRound className="w-4 h-4 mr-2" />Entrar al panel</>
                  )}
                </Button>

                {/* Reenviar */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resending || resendCooldown > 0}
                    data-testid="admin-resend-otp-btn"
                    className="text-xs text-muted-foreground hover:text-slate-300 transition-colors
                               flex items-center gap-1.5 mx-auto disabled:opacity-40"
                  >
                    {resending ? (
                      <><Loader2 className="w-3 h-3 animate-spin" />Enviando...</>
                    ) : resendCooldown > 0 ? (
                      <><RefreshCw className="w-3 h-3" />Reenviar código ({resendCooldown}s)</>
                    ) : (
                      <><RefreshCw className="w-3 h-3" />Reenviar código</>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-muted-foreground text-xs mt-6">
          ¿Eres cliente?{" "}
          <a href="/login" className="text-muted-foreground hover:text-slate-400 underline">
            Inicia sesión aquí
          </a>
        </p>
      </div>
    </div>
  );
}
